import { __awaiter } from "tslib";
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
function log(message) {
    // eslint-disable-next-line no-console
    console.log(`[WebSerialTransport] ${message}`);
}
// Authentic Arduinos report this productId and are the only tested boards that raise the
// browser `connect` event, which the UI uses to track physical connection independently
// of port state.
const kArduinoUsbProductId = 67;
export class WebSerialTransport {
    constructor() {
        Object.defineProperty(this, "onData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onDisconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceFamily", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "port", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "writer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "reading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "READ_TIMEOUT_MS", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2000
        });
    }
    // Whether this board raises the browser `connect`/`disconnect` events (see
    // initWebSerialConnectionEvents). Only authentic Arduinos were observed to raise them.
    get raisesWebSerialConnect() {
        var _a;
        return ((_a = this.deviceInfo) === null || _a === void 0 ? void 0 : _a.usbProductId) === kArduinoUsbProductId;
    }
    hasOpenPort() {
        return this.port != null && this.port.readable != null;
    }
    determineDeviceFamily(info) {
        return info.usbProductId === 516 && info.usbVendorId === 3368 ? "microbit" : "arduino";
    }
    // requestPort + open + resolve deviceFamily/deviceInfo. Returns false if the user
    // cancels the chooser or the port cannot be opened for writing.
    open() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.port = yield navigator.serial.requestPort();
                this.deviceInfo = yield this.port.getInfo();
                this.deviceFamily = this.determineDeviceFamily(this.deviceInfo);
            }
            catch (error) {
                console.error("error requesting port: ", error);
                return false;
            }
            yield this.port.open({ baudRate: 9600 }).catch((e) => console.error(e));
            if (!this.port.writable) {
                console.error("Port is not writable");
                yield this.closePort();
                return false;
            }
            this.writer = this.port.writable.getWriter();
            return true;
        });
    }
    write(line) {
        var _a;
        if (this.hasOpenPort()) {
            (_a = this.writer) === null || _a === void 0 ? void 0 : _a.write(textEncoder.encode(`${line}\n`));
        }
        else {
            log("Port closed, skipping write");
        }
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            this.reading = false;
            yield this.closePort();
            this.port = null;
        });
    }
    // Read loop. Delivers decoded chunks to onData; on give-up (reopen fails) fires
    // onDisconnect. Preserves the prior reopen/timeout recovery behavior. Relies on the
    // browser nulling `port.readable` on a real disconnect to drive the reopen check below.
    startReading() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Not proactively stopped by the navigator.serial "disconnect" event; it self-terminates
            // within one reopen cycle once the port stops being readable (see the timeout/reopen logic below).
            this.reading = true;
            while (this.reading && this.port) {
                if (!this.port.readable) {
                    log("Port not readable, attempting to reopen...");
                    const reopened = yield this.reopenPort();
                    if (!reopened || !this.port.readable) {
                        console.error("Failed to reopen port, stopping stream handler");
                        break;
                    }
                }
                const streamReader = this.port.readable.getReader();
                try {
                    while (this.reading && this.port.readable) {
                        const { value, done, timedOut } = yield this.readWithTimeout(streamReader);
                        if (timedOut) {
                            log("Read timed out, closing and reopening port...");
                            yield this.closePort(streamReader);
                            break;
                        }
                        if (done)
                            break;
                        (_a = this.onData) === null || _a === void 0 ? void 0 : _a.call(this, value);
                    }
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    // The streamReader might have already been released by closePort().
                    // If it hasn't, we need to release it here so we can get a new reader later.
                    // It is idempotent so it is safe to call a second time.
                    streamReader.releaseLock();
                }
            }
            // If we exited the loop without an explicit stop (close()), the connection failed.
            if (this.reading) {
                this.reading = false;
                (_b = this.onDisconnect) === null || _b === void 0 ? void 0 : _b.call(this);
            }
        });
    }
    readWithTimeout(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            let timeoutId;
            const timeoutPromise = new Promise((resolve) => {
                timeoutId = setTimeout(() => resolve({ value: undefined, done: false, timedOut: true }), this.READ_TIMEOUT_MS);
            });
            const readPromise = streamReader.read().then(({ value, done }) => ({
                value: value ? textDecoder.decode(value) : undefined,
                done,
                timedOut: false
            }));
            // Clear the timer once the race settles so resolved reads don't leave pending timeouts firing.
            return Promise.race([readPromise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
        });
    }
    closePort(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return;
            try {
                if (streamReader) {
                    try {
                        yield streamReader.cancel();
                    }
                    catch (e) { /* reader may already be released */ }
                    try {
                        streamReader.releaseLock();
                    }
                    catch (e) { /* lock may already be released */ }
                }
                if (this.writer) {
                    try {
                        yield this.writer.close();
                    }
                    catch (e) { /* writer may already be closed */ }
                }
                yield this.port.close();
            }
            catch (e) {
                console.error("Error closing port:", e);
            }
        });
    }
    reopenPort() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return false;
            try {
                yield this.port.open({ baudRate: 9600 });
                this.writer = this.port.writable.getWriter();
                return true;
            }
            catch (e) {
                console.error("Error reopening port:", e);
                return false;
            }
        });
    }
}
/**
 * Some serial devices fire the `connect` event before we call requestPort (the browser raises
 * it for devices the origin already has permission for), so we register handlers at CLUE
 * startup to catch this event and improve the connect-button UI. Not every board does this —
 * see raisesWebSerialConnect in WebSerialTransport.
 *
 * The disconnect handler tears down the active connection on unplug. It is guarded so it only
 * fires while a WebSerialTransport is active and can't kill a live WebUSB (Spiker:bit) session.
 */
export function initWebSerialConnectionEvents(serialDevice) {
    var _a, _b;
    (_a = navigator.serial) === null || _a === void 0 ? void 0 : _a.addEventListener("connect", (e) => {
        serialDevice.updateConnectionInfo(e.timeStamp, e.type);
    });
    (_b = navigator.serial) === null || _b === void 0 ? void 0 : _b.addEventListener("disconnect", (e) => {
        if (serialDevice.activeTransport instanceof WebSerialTransport) {
            serialDevice.updateConnectionInfo(e.timeStamp, e.type);
            serialDevice.clearActiveDevice();
        }
    });
}
//# sourceMappingURL=data:application/json;base64,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